import { Flex, Typography } from 'antd'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getLookupValues } from 'src/app/store/masterSetup'
import { colorBorderDisabled } from 'src/app/themes/primaryThemeDefault'
import { Control, Controller, UseFormReturn } from 'react-hook-form'
import { inputNumber } from 'src/app/shared/utils'
import { NewAccountFormValues } from '../models'
import { useFormRules } from '../utils'
import { PROCESSING_TYPES } from '../constants'
import CustomSelect from 'src/app/shared/components/common/select'
import CustomInput from 'src/app/shared/components/common/input'
import CustomCheckbox from 'src/app/shared/components/common/checkbox'
import { IClient } from 'src/app/shared/models/clientSetup.model'
import { useWindowSize } from 'src/app/shared/hooks/useWindowSize'

export interface CheckFormProps {
  control: Control<NewAccountFormValues>
  loading: boolean
  checkExceptionProcessingType: string
  form: UseFormReturn<NewAccountFormValues>
  disabledForm?: boolean
  client?: IClient | null
  isAddAccount?: boolean
  isEditMode?: boolean
}

const CheckForm: React.FC<CheckFormProps> = (props) => {
  const {
    client,
    control,
    loading,
    checkExceptionProcessingType,
    form,
    disabledForm,
    isAddAccount = false,
    isEditMode = false,
  } = props
  const { t } = useTranslation()
  const rules = useFormRules()
  const lookupValues = useSelector(getLookupValues)
  const { mode } = useWindowSize()
  const isPayeeMatchDisabled = useMemo(() => {
    if (isEditMode) {
      return false
    }
    return !client?.payeeMatch
  }, [client?.payeeMatch, isEditMode])

  return (
    <Flex
      style={{
        flexDirection: 'column',
      }}
    >
      <Typography.Text
        strong
        style={{
          fontSize: 16,
          fontWeight: 700,
        }}
      >
        {t('AccountSetup:new_account_form.title.checkExceptions')}
      </Typography.Text>
      <Flex
        style={{
          borderColor: colorBorderDisabled,
          borderWidth: 1,
          borderStyle: 'solid',
          borderRadius: 16,
          padding: 16,
          flexDirection: 'column',
          gap: 24,
          marginTop: 4,
        }}
      >
        <Controller
          control={control}
          name={'checkExceptionProcessingType'}
          render={({ field, fieldState }) => (
            <CustomSelect
              label={t('AccountSetup:new_account_form.title.checkExceptionProcessingType')}
              placeholder={t('AccountSetup:new_account_form.placeholder.checkExceptionProcessingType')}
              value={!field.value ? null : field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error}
              options={lookupValues?.DefaultCheckExceptionCreationType ?? []}
              loading={loading}
              disabled={
                loading ||
                disabledForm ||
                (client?.defaultCheckExceptionCreationType === PROCESSING_TYPES.NOCREATE)
              }
            />
          )}
          rules={rules.checkExceptionProcessingType}
        />
        <Flex
          style={{
            gap: 24,
            display: checkExceptionProcessingType === PROCESSING_TYPES.NOCREATE ? 'none' : 'flex',
            flexDirection: ['mobile', 'tablet'].includes(mode) ? 'column' : undefined,
          }}
        >
          <Controller
            control={control}
            name={'checkDecision'}
            render={({ field, fieldState }) => (
              <CustomSelect
                label={t('AccountSetup:new_account_form.title.checkDecision')}
                placeholder={t('AccountSetup:new_account_form.placeholder.checkDecision')}
                value={!field.value ? null : field.value}
                onChange={field.onChange}
                onBlur={async () => {
                  field.onBlur()
                  await form.trigger('checkDecision')
                }}
                error={fieldState.error}
                options={lookupValues?.DefaultCheckDecision ?? []}
                loading={loading}
                required={form.getValues('checkExceptionProcessingType') === PROCESSING_TYPES.CREATE}
                disabled={disabledForm}
              />
            )}
            rules={{
              required: {
                value: form.getValues('checkExceptionProcessingType') === PROCESSING_TYPES.CREATE,
                message: t('AccountSetup:new_account_form.validation.checkDecisionRequired'),
              },
            }}
          />
          <Controller
            control={control}
            name={'checkReason'}
            render={({ field, fieldState }) => (
              <CustomSelect
                label={t('AccountSetup:new_account_form.title.checkReason')}
                placeholder={t('AccountSetup:new_account_form.placeholder.checkReason')}
                value={!field.value ? null : field.value}
                onChange={field.onChange}
                onBlur={async () => {
                  field.onBlur()
                  await form.trigger('checkReason')
                }}
                error={fieldState.error}
                options={lookupValues?.DefaultCheckReason ?? []}
                loading={loading}
                required={form.getValues('checkExceptionProcessingType') === PROCESSING_TYPES.CREATE}
                disabled={disabledForm}
              />
            )}
            rules={{
              required: {
                value: form.getValues('checkExceptionProcessingType') === PROCESSING_TYPES.CREATE,
                message: t('AccountSetup:new_account_form.validation.checkReasonRequired'),
              },
            }}
          />
        </Flex>
        <Flex style={{
          display: checkExceptionProcessingType === PROCESSING_TYPES.NOCREATE ? 'none' : 'flex',
          flexDirection: 'column',
          }}>
        <Flex
          style={{
            flexDirection: 'column',
            gap: 24,
            display: checkExceptionProcessingType === PROCESSING_TYPES.NOCREATE ? 'none' : 'flex',
          }}
        >
          <Controller
            control={control}
            name={'staleDatedDays'}
            render={({ field, fieldState }) => (
              <CustomInput
                label={t('AccountSetup:new_account_form.title.staleDatedDays')}
                placeholder={t('AccountSetup:new_account_form.placeholder.staleDatedDays')}
                value={field.value}
                onChange={(e) => field.onChange(inputNumber(e), { shouldValidate: true })}
                onBlur={async () => {
                  field.onBlur()
                  await form.trigger('staleDatedDays')
                }}
                error={fieldState.error}
                maxLength={(rules.staleDatedDays.maxLength as any).value}
                max={(rules.staleDatedDays.max as any).value}
                min={(rules.staleDatedDays.min as any).value}
                disabled={disabledForm}
              />
            )}
            rules={rules.staleDatedDays}
          />
          <Controller
            control={control}
            name={'payeeMatch'}
            render={({ field, fieldState }) => (
              <CustomCheckbox
                label={t('AccountSetup:new_account_form.title.payeeMatch')}
                checked={!!field.value}
                onChange={field.onChange}
                error={fieldState.error}
                disabled={isPayeeMatchDisabled}
              />
            )}
            rules={rules.payeeMatch}
          />
        </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}

export default CheckForm
